<template>
  <div class="totals-cnt">
    <ul class="totals">
      <li>
        <p>{{ $t('viewOrder.total_items_in_order') }}</p>
        <span>{{ totalItems }}</span>
      </li>
      <li>
        <p>{{ $t('viewOrder.order_total') }}</p>
        <span :class="{ 'order-total-disable': totalPrice === 0 }">{{ totalPriceText }}</span>
      </li>
      <li v-if="showGrossTotal" class="label">
        <p>{{ $t('viewOrder.order_gross_total_message') }}</p>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { DraftModel } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import { useFormat } from '../../composables/format';
import { Order } from '../../models/order.model';
import { ValidatorResult } from '../../models/validation-result.model';
import { DraftsService } from '../../services/drafts.service';
import { useUserStore } from '../../stores/user';

export default defineComponent({
  name: 'view-order-totals',
  props: {
    order: {
      type: Object,
      default: undefined,
    },
  },
  setup() {
    const { selectedAccount } = storeToRefs(useUserStore());
    const { formatCurrency, formatDate } = useFormat();

    return {
      account: selectedAccount,
      formatCurrency,
      formatDate,
    };
  },
  data() {
    return {
      draftsService: new DraftsService(),
      fetchingPrices: false,
      orderDetails: this.order as unknown as Order,
    };
  },
  computed: {
    totalItems(): number {
      return this.orderDetails.products?.reduce(
        (total: number, product: { quantity: number }) => total + Math.round(product.quantity),
        0,
      );
    },
    totalPriceText(): string {
      if (this.totalPrice === 0) {
        return this.$t('viewOrder.not_available');
      }

      return this.currencyFormatter(this.totalPrice);
    },
    totalPrice(): number {
      return this.orderDetails?.total ?? 0;
    },
    showGrossTotal(): boolean {
      return this.totalPrice >= 0;
    },
  },
  mounted() {
    if (this.orderDetails?.pricesAsync) {
      this.fetchingPrices = true;
      this.draftsService
        .getPrices(this.orderDetails?.id, this.account.id)
        .then((pricesResult: ValidatorResult<DraftModel>) => {
          if (pricesResult.model) {
            const orderWithPrices = pricesResult.model;

            this.orderDetails.total = orderWithPrices.total;

            this.orderDetails.products.forEach((product) => {
              const productWithPrice = orderWithPrices.products.find(
                (pricesResultProduct) => pricesResultProduct.productId === product.productId,
              );

              if (productWithPrice) {
                product.unitPrice = productWithPrice.unitPrice;

                if (productWithPrice.totalPrice) {
                  product.totalPrice = productWithPrice.totalPrice;
                }
              }
            });
          }
        })
        .finally(() => {
          this.fetchingPrices = false;
        });
    }
  },
  methods: {
    currencyFormatter(value: number) {
      if (this.orderDetails.pricesAsync && this.fetchingPrices) {
        return '<div class="grid-column-loader" />';
      }

      return this.formatCurrency(this.orderDetails?.currencyCode, value);
    },
  },
});
</script>
